import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api, type CaseSummary } from '../api/client.js';

/**
 * The dispatch board.
 *
 * Three columns, one per pipeline, because a case genuinely sits in all three at
 * once — Completed in Ops while still New for QC and untouched by Billing. A
 * single-column board would have to pick one of those to show and lie about the
 * other two.
 */

const OPS_STAGES = [
  { key: 'new_call', name: 'New Call', colour: '#DC2626' },
  { key: 'dispatched', name: 'Dispatched', colour: '#EA580C' },
  { key: 'on_scene', name: 'On Scene', colour: '#CA8A04' },
  { key: 'transporting', name: 'Transporting', colour: '#2563EB' },
];

const PRIORITY_STYLE: Record<string, string> = {
  p1: 'bg-red-500/15 text-red-300 ring-red-500/30',
  p2: 'bg-orange-500/15 text-orange-300 ring-orange-500/30',
  p3: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  p4: 'bg-slate-500/15 text-slate-300 ring-slate-500/30',
};

function elapsed(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return hours < 24 ? `${hours}h ${mins % 60}m` : `${Math.floor(hours / 24)}d`;
}

function CaseCard({ c }: { c: CaseSummary }) {
  return (
    <Link
      to={`/cases/${c.id}`}
      className="block rounded-lg border border-line bg-surface-3 p-3 transition hover:border-slate-500 hover:bg-slate-800/60"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-sm text-slate-100">{c.case_number}</span>
        {c.priority && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ring-1 ${
              PRIORITY_STYLE[c.priority] ?? PRIORITY_STYLE['p4']
            }`}
          >
            {c.priority}
          </span>
        )}
      </div>
      {c.from_address && (
        <p className="mt-1.5 line-clamp-2 text-xs text-slate-400">{c.from_address}</p>
      )}
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
        <span>{c.department_name ?? c.case_type}</span>
        {/* Elapsed time, not a timestamp. In a control room the question is
            always "how long has this been waiting", never "what time was it". */}
        <span className="tabular-nums">{elapsed(c.requested_at)}</span>
      </div>
    </Link>
  );
}

function Column({ title, colour, cases }: { title: string; colour: string; cases: CaseSummary[] }) {
  return (
    <div className="flex min-w-[240px] flex-1 flex-col">
      <div className="mb-2 flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: colour }} />
        <h2 className="text-sm font-medium text-slate-300">{title}</h2>
        <span className="ml-auto rounded bg-slate-700/50 px-1.5 text-xs tabular-nums text-slate-400">
          {cases.length}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {cases.map((c) => (
          <CaseCard key={c.id} c={c} />
        ))}
        {cases.length === 0 && (
          <p className="rounded-lg border border-dashed border-line p-3 text-center text-xs text-slate-500">
            Nothing here
          </p>
        )}
      </div>
    </div>
  );
}

export function BoardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['cases'],
    queryFn: () => api.get<{ cases: CaseSummary[] }>('/cases?limit=200'),
    // Polls while open. SSE replaces this in the realtime work package; polling
    // is honest and simple until there is something to push.
    refetchInterval: 15_000,
  });

  if (isLoading) return <p className="p-6 text-sm text-slate-400">Loading board…</p>;
  if (error) return <p className="p-6 text-sm text-red-400">{error.message}</p>;

  const cases = data?.cases ?? [];
  const open = cases.filter((c) => c.status === 'open' || c.status === 'on_hold');

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-semibold text-slate-100">Dispatch</h1>
        <span className="text-sm text-slate-400">
          {open.length} active · {cases.length} total
        </span>
        <NewCallButton />
      </div>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
          EMS Operations
        </h2>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {OPS_STAGES.map((s) => (
            <Column
              key={s.key}
              title={s.name}
              colour={s.colour}
              cases={open.filter(
                (c) => c.ops_stage_key === s.key || (s.key === 'new_call' && !c.ops_stage_key),
              )}
            />
          ))}
        </div>
      </section>

      {/* Downstream queues. Shown alongside rather than below, because they are
          concurrent with operations, not a later phase of it. */}
      <div className="grid gap-6 md:grid-cols-2">
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Clinical QC
          </h2>
          <Column
            title="Awaiting review"
            colour="#7C3AED"
            cases={cases.filter((c) => !c.qc_stage_key || c.qc_stage_key === 'new_for_qc')}
          />
        </section>
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Billing
          </h2>
          <Column
            title="Awaiting invoice"
            colour="#0891B2"
            cases={cases.filter(
              (c) => !c.billing_stage_key || c.billing_stage_key === 'new_for_billing',
            )}
          />
        </section>
      </div>
    </div>
  );
}

/**
 * Start a case and go straight to its call card.
 *
 * Deliberately no dialog. A call is already ringing; the fastest path is to
 * create the record and start typing into the real form, which autosaves. The
 * case number is allocated by the server, so two call-takers pressing this at
 * the same moment cannot collide.
 */
function NewCallButton() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<{ id: string }>('/cases', {
        caseType: 'ems_road',
        origin: 'phone',
      });
      void navigate(`/cases/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start a case');
      setBusy(false);
    }
  };

  return (
    <span className="ml-auto flex items-center gap-2">
      {error && <span className="text-xs text-red-400">{error}</span>}
      <button
        type="button"
        onClick={() => void start()}
        disabled={busy}
        className="rounded bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-50"
      >
        {busy ? 'Starting…' : '+ New call'}
      </button>
    </span>
  );
}
